import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { sendCallback } from "@/lib/send-callback.functions";

export function CallbackForm({ id }: { id?: string }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", pincode: "" });
  const send = useServerFn(sendCallback);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.phone) || !/^\d{6}$/.test(form.pincode)) {
      toast.error("Please enter a valid name, 10-digit phone, and 6-digit pincode.");
      return;
    }
    setLoading(true);
    try {
      await send({ data: form });
      const w = window as unknown as { dataLayer?: unknown[] };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ event: "form_submit_lead" });
      setForm({ name: "", phone: "", pincode: "" });
      // Full page load so GTM / Google Ads fires a fresh page_view on /thank-you
      window.location.assign("/thank-you");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit right now. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div id={id} className="rounded-3xl bg-card p-6 text-card-foreground shadow-[var(--shadow-elevated)] sm:p-8">
      <h2 className="text-center text-xl font-bold sm:text-2xl">
        Book DTH New Connection now
      </h2>
      <p className="mt-2 text-center text-sm text-muted-foreground">Fill out this form to arrange a callback!</p>
      <form onSubmit={onSubmit} className="mt-6 space-y-3">
        <Field label="Full Name" name="name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Your full name" />
        <Field label="Phone" name="phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v.replace(/\D/g, "").slice(0, 10) })} placeholder="10-digit mobile" inputMode="numeric" />
        <Field label="Pincode" name="pincode" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v.replace(/\D/g, "").slice(0, 6) })} placeholder="6-digit pincode" inputMode="numeric" />
        <button
          type="submit"
          disabled={loading}
          className="mt-2 w-full rounded-xl bg-accent px-5 py-3.5 text-base font-semibold text-accent-foreground shadow-[var(--shadow-glow)] transition hover:brightness-110 disabled:opacity-60"
        >
          {loading ? "Submitting..." : "Request Callback"}
        </button>
        <p className="pt-1 text-center text-xs text-muted-foreground">We respect your privacy. No spam, just a callback.</p>
      </form>
    </div>
  );
}

function Field({
  label, name, value, onChange, placeholder, inputMode,
}: {
  label: string; name: string; value: string; onChange: (v: string) => void; placeholder?: string; inputMode?: "numeric" | "text";
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-foreground">{label}</span>
      <input
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        className="w-full rounded-xl border border-input bg-muted/40 px-4 py-3 text-base outline-none transition focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/30"
        required
      />
    </label>
  );
}
